import React, { createContext } from 'react';
import { ThemeProvider } from 'styled-components';

// Import your custom hooks and components
import { useGet } from './hooks/useFetch';
import { getLocalizationData } from './lang/getLocalization';
import FormWrapper from './FormWrapper';
import QRcode from './components/QRcode/QRcode';
import { getTheme } from './utils/Themes';

import TwoColumnView from './components/SkeletonLoaders/TwoColumnView';

// Create the AppContext for your application
export const AppContext = createContext();

// Helper function to parse URL parameters
const getUrlParams = (url = window.location) => {
  let params = {};

  new URL(url).searchParams.forEach((val, key) => {
    params[key] = val;
  });

  return params;
};

// Helper function to handle preview mode status
const getPreviewMode = (urlParams) => {
  if (urlParams.preview === 'true' && urlParams.previewToken === process.env.REACT_APP_PREVIEW_TOKEN) {
    return true;
  } else return false;
};

// Main App component
const App = ({channelId}) => {
  const path = window.location.pathname;
  const parts = path.split('/').filter(Boolean);
  const formUID = parts[parts.length - 1];
  const urlParams = getUrlParams();
  const inPreviewMode = getPreviewMode(urlParams);

  const localization = getLocalizationData(channelId);
  const strapiUrl = process.env.REACT_APP_STRAPI_URL;

  // Fetch data using custom hook
  const { loading, error, apiResponse } = useGet(
    `${strapiUrl}/api/forms?locale=${
      localization.language
    }&filters[UID][$eq]=${formUID}&status=live&populate=*`
  ); // Get form by UID, filtered by language. If preview mode = true, allow display of unpublished forms

  // Handle loading and error states
  if (!formUID) return <p>{localization.text.errors.noFormUID}</p>;
  if (loading) return <TwoColumnView />;
  if (error) return <p>Error: {error}</p>;

  const data = apiResponse?.data[0];

  // Handle missing data
  if (!data) return <p>{localization.text.errors.noData}</p>;

  // Check if form is currently available
  const now = Date.now();
  const startDate = Date.parse(data.startdate);
  const endDate = Date.parse(data.enddate);

  if (now < startDate) return <p>{localization.text.formNotStarted}</p>;
  if (now > endDate) return <p>{localization.text.formExpired}</p>;

  const rootElement = document.getElementById('react-forms-app');
  const scensumBaseUrl = rootElement.getAttribute('data-scensum-base-url');
  const assetBaseUrl = rootElement.getAttribute('data-asset-base-url');
  const webhookUrl = rootElement.getAttribute('data-webhook-url');

  // Create the context provider
  const AppProvider = ({ children }) => (
    <AppContext.Provider
      value={{
        channel: channelId,
        scensumBaseUrl: scensumBaseUrl,
        assetBaseUrl: assetBaseUrl,
        urlParams: urlParams,
        localization,
        formAttributes: { id: data.id, ...data },
        previewMode: inPreviewMode,
        webhookUrl: webhookUrl,
      }}
    >
      {children}
    </AppContext.Provider>
  );

  const appTheme = getTheme(channelId);

  return (
    <div>
      {/* Render the FormWrapper component with channelId */}
      <AppProvider>
        {/* Apply styled-components theme */}
        <ThemeProvider theme={appTheme}>
          {/* Conditional rendering of QR code based on params */}
          {urlParams.hasOwnProperty('qr') ? <QRcode /> : <FormWrapper />}
        </ThemeProvider>
      </AppProvider>
    </div>
  );
};

export default App;
